<template>
  <GcBaseLayout
    :title="'Scheme Prices'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Scheme Prices'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Scheme Prices'"
        :secondaryLabel="undefined"
      />
    </template>
    <div class="split-view-template css-v2 split-view scheme-prices">
      <GcSplitView
        :showLeftContainer="true"
        :showRightContainer="showRightPanel"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :dataSourceConfig="leftGridConfig.gridData.gridOptions.dataSource"
              :columns="leftGridConfig.gridData.columns"
              :gridToolbar="leftGridConfig.gridData.toolbar"
              :padColumn="true"
              :id="'scheme-prices-left-grid'"
              :rowClick="onLeftRowClick"
              :exportFileName="leftGridConfig.exportFileName"
              :heightPadding="20"
              :paging="false"
              ref="leftGrid"
              :filterHeightAdjustment="-10"
              v-if="loaded"
            />
          </div>
        </template>

        <template #rightContent>
          <div class="right-panel-container" v-if="showRightPanel">
            <GcFormHeaderToolbar
              :toolbarOptions="rightToolbarOptions"
              :dataModel="toolbarRecord"
              :baseView="baseView"
              :backRoute="leftGridConfig.pageRoute"
              :itemSaveCallBack="onSaveClicked"
            />

            <div class="selected-record-info">
              <GcForm
                :formOptions="{ gcLabelLocation: 'left' }"
                :formItems="identityFormItems"
                :isNormal="true"
                :data-model="identityDataModel"
                :formMode="baseView.mode"
                :formLocation="'normal'"
                :formHeight="identityFormHeight"
              />
            </div>

            <!-- Story 3 ("2116 - UI - Add New Season/Scheme entry fields and
                 START duplicate check"): the grid also shows in create mode
                 once START has locked a fresh Season+Scheme identity in
                 (createIdentityLocked) - before that, only the live entry
                 fields above (identityFormItems' create-mode branch) show. -->
            <div
              class="view-card responsive-paddings split-view-right-grid"
              v-if="baseView.mode !== 'create' || createIdentityLocked"
            >
              <DxDataGrid
                :data-source="priceRows"
                key-expr="RowKey"
                :show-borders="false"
                :repaint-changes-only="true"
                :column-auto-width="true"
                no-data-text="No price rows recorded for this Scheme+Season."
                :columnChooser="{ enabled: false }"
                :height="getHeight"
                @content-ready="onGridContentReady"
                ref="rightGrid"
                :columns="getEditableColumns"
                @editor-preparing="onEditorPreparing"
                @key-down="onGridKeyDown"
                @row-prepared="onPriceRowPrepared"
              >
                <DxLoadPanel :show-indicator="false" :enabled="false" />
                <DxPaging :page-size="50" :enabled="false" />
                <DxPager :visible="false" />
                <!-- No manual "Add Row" - CX convention (developer feedback,
                     2026-09-03): a blank row is always auto-present at the
                     end while editing instead (ensureTrailingBlankRow). -->
                <DxEditing
                  mode="cell"
                  :allow-updating="isEditing"
                  :allow-adding="false"
                  :allow-deleting="isEditing"
                  :use-icons="true"
                  :select-text-on-edit-start="true"
                />

                <template #priceTypeCellTemplate="{ data }">
                  <span>{{ priceTypeDisplayText(data.data.PriceType) }}</span>
                </template>
                <template #varietyCellTemplate="{ data }">
                  <span>{{ varietyDisplayText(data.data.VarietyCode) }}</span>
                </template>
                <template #itemCodeCellTemplate="{ data }">
                  <span>{{ itemCodeDisplayText(data.data) }}</span>
                </template>
              </DxDataGrid>

              <div class="split-grid-footer">
                <span>Row Count: {{ realPriceRowCount }}</span>
              </div>
            </div>
          </div>
        </template>
      </GcSplitView>
    </div>
  </GcBaseLayout>
</template>

<script>
/*
Description
    scheme-prices.vue - Settings > Intake > Scheme Prices (Project 2116,
    Almondco Intake Masters Migration). List + filter panel (#76605) - the
    List (Season/Scheme/Price Rows/Last Changed) and its filter panel (Season
    fixed 3-value tag box, Scheme distinct-values-in-use tag box), plus enough
    route/identity-resolution scaffolding for a List row click to open the
    split-view panel in Review mode showing the Season/Scheme identity
    (user-stories.md Story 1's own smoke-check AC).

    #76635 (Story 2 - Maintain & Save): the real editable detail grid - Price
    Type (fixed 1/2/3 list)/Variety (AlmondcoIntakeVariety, Inactive eq 'N'
    only - the "non-packaging" half of BR-06 stays the already-documented
    open gap, technical-design.md §4/§11)/Item Code (AlmondcoIntakeVarietySize
    Product, a fresh DataSource built per row in onEditorPreparing, scoped to
    that row's own selected VarietyCode from the moment it's created - never
    a shared/cached instance, see onEditorPreparing's own comment; corrected
    2026-09-02 after developer feedback caught the first version's shared
    DataSource starting out unscoped)/YTD Price
    (decimal, 4dp, min 0) - plus Remove rows and Save (POST
    api/Save_AlmondcoSchemePrice, #76631). Uses a raw devextreme-vue
    DxDataGrid, not GcDataGrid - same reasoning as grower-pool-splits.vue's
    own header comment: GcDataGrid v1 hardcodes allow-updating + mode="cell"
    with no allowAdding/allowDeleting support. One remaining deliberate
    deviation from the sibling split screens, flagged rather than silently
    carried over:
      - setRightGridHeight queries ".split-container-child.right-container",
        verified directly against gc-split-view.vue's real template this
        session - grower-syndicate-splits.vue's own equivalent method queries
        ".filter-split-container", which does not match and so silently
        no-ops there.

    Row-adding mechanic corrected 2026-09-03 (developer feedback - CX
    convention, not a numbered ADO task): CX screens do not expose a manual
    "Add Row" button - a blank row is always auto-added (on entering Edit
    mode with existing rows, or immediately once Add New's START locks an
    identity in), and once that blank row's own Price Type is set, a fresh
    blank row auto-appends after it, so there is always exactly one blank
    row waiting at the end while editing. This session's first version got
    this wrong: it kept DxEditing's native "+" button (relabelled "Add Price
    Row" via onGridToolbarPreparing) and never auto-added anything, reasoned
    at the time as unavoidable because PriceType always has a real value here
    (defaults to "1") unlike the sibling grids' GrowerCode=null sentinel - an
    auto-added Type-1 row would have been indistinguishable from a
    legitimate one. The actual fix: a blank row's PriceType is `null` (never
    defaulted), not "1" - `null` is exactly the sentinel this grid needed all
    along; validateBeforeSave/onSaveClicked filter out any row still sitting
    at PriceType `null` so a trailing never-filled-in blank never blocks Save
    or reaches the server. See ensureTrailingBlankRow's own comment for the
    reactive mechanics (a single deep watch on priceRows, not scattered
    per-call-site logic) and onEditorPreparing's own comment for why Variety
    must now also be disabled when PriceType is null, not just "1".

    Reworked 2026-09-01 (#77290/#77291 follow-up, developer direction): the
    List's per-Scheme+Season summary and the Scheme filter's distinct-value
    option list are no longer derived client-side from the full raw
    api/AlmondcoSchemePrice row set (the original AD-05 approach) - both now
    read directly from the new api.AlmondcoSchemePriceSummary view/endpoint.
    AD-05's premise (fetch-and-group-client-side is fine) was invalidated by a
    real scaling concern: this table grows by thousands of rows/year, and
    grouping every raw row in the browser doesn't scale. Not yet reflected in
    technical-design.md itself - a follow-up doc task, not done as part of
    this rework.

    Route identity - RecordId, not the natural SchemeId+SeasonYear composite.
    Originally (2026-09-01, first pass at this task) this screen used a
    purely client-composed key (`${SeasonYear}-${SchemeId}`), since AD-04 had
    reasoned no server-computed RecordId existed for this natural key (no
    summary view existed yet). AlmondcoSchemePriceSummary now exposes exactly
    that - RecordId, a deterministic GUID computed in the view from
    SchemeId+SeasonYear (HASHBYTES('MD5', ...), same shape as
    AlmondcoGrowerSyndicateSplitSummary's own RecordId) - so this screen now
    routes on it directly, matching grower-syndicate-splits.vue's own
    mechanism exactly: onLeftRowClick keys on RecordId, loadSelectedRecord
    resolves the SchemeId/SeasonYear identity via a $filter=RecordId eq {id}
    lookup against the summary endpoint. This also eliminates the one
    previously-flagged risk with the client-composed key (a Scheme code
    containing "/" would have broken the URL path) - RecordId is a plain GUID,
    always URL-safe, regardless of what SchemeId itself contains.

    Filter panel field resolution (§6/§10):
      - Season -> fixed 3-value set (current FY-1/FY/FY+1), NOT the
        AlmondcoIntakeSeason-backed screenField the sibling split screens use
        for their own Season filter (that resolves against the full season
        master list) - technical-design.md is explicit this screen's Season
        filter is a fixed 3-value set, not a master-list lookup. "Current FY"
        itself is still resolved the same way console-season-year.vue's own
        setDefaultSeasonYear does (the AlmondcoIntakeSeason row whose date
        range contains today) - reuse-before-create for that one piece.
      - Scheme -> distinct SchemeId values already in use, sourced from a
        dedicated small lookup endpoint (api/AlmondcoSchemeSearch - a plain
        SELECT DISTINCT over api.AlmondcoSchemePrice) via GcDataGrid's own
        remote dataSourceConfig, queried only when the filter dropdown opens -
        NOT fetched-and-deduplicated client-side from AlmondcoSchemePriceSummary
        on screen load, which the developer flagged as its own scaling
        concern (that endpoint grows with every Scheme+Season combination
        ever recorded, and was being fetched in full just to extract a
        handful of distinct codes). Same class of fix as
        grower-pool-splits.vue's own AlmondcoGrowerPoolSearch (#75930) - the
        one difference being Scheme has no separate master table to join
        against at all (FLAG-CONCEPT-01), so there is no description to show
        alongside the code, only the bare code itself. Both filter columns
        use the established screenField: "ColumnValue" placeholder trick (see
        bin-comments.vue's own Site column) purely to satisfy gc-form.vue's
        screenField resolution requirement for filter fields - it carries no
        meaning of its own here, matching that precedent exactly.

    Documentation discrepancy flagged, not silently resolved: test-cases.md's
    TC-006 expects the List's Scheme column to show a friendly description
    ("Standard Almond Scheme") as primary text with the code secondary,
    matching common.md's coded-lookup display convention. No such description
    exists anywhere in the real data model - FLAG-CONCEPT-01/file-list.md both
    confirm no Scheme master of any kind exists in this migration, and
    TC-006's own "Standard Almond Scheme" text is test-data fiction from test-
    cases.md's own worked example, not a real column. The Scheme column here
    renders the bare code only, single line - TC-006 needs correcting against
    the developer/PO rather than an invented description being built to
    satisfy it.

History
    01Sep2026 (SD)
        - #76605: 2116 - UI - Scheme Prices List and filter panel
        - #77290/#77291 follow-up: reworked to bind against the new
          api.AlmondcoSchemePriceSummary view/endpoint instead of deriving the
          List's summary and the Scheme filter's option list client-side from
          the full raw row set, and to route on the summary's own
          server-computed RecordId instead of a client-composed key.
    02Sep2026 (SD)
        - Follow-up (developer-flagged scaling concern): the Scheme filter's
          distinct-value option list was still fetching AlmondcoSchemePriceSummary
          in full on screen load and deduplicating client-side - that endpoint
          grows with every Scheme+Season combination ever recorded, so this
          doesn't scale either. Replaced with a dedicated
          api/AlmondcoSchemeSearch endpoint (SELECT DISTINCT over
          api.AlmondcoSchemePrice), queried remotely by GcDataGrid only when
          the filter dropdown opens - same fix pattern as
          AlmondcoGrowerPoolSearch (#75930).
        - #76635: 2116 - UI - Scheme Prices detail panel (Review/Edit grid).
          Real editable Price Type/Variety/Item Code/YTD Price grid,
          Add/Remove rows, and Save - see the Description block above for the
          two deliberate deviations from the sibling split screens.
        - #76635 follow-up (developer-flagged scaling concern): the Item Code
          column's first version created a single shared DataSource once in
          created() and re-filtered it in place per row - but that instance
          has to exist (and therefore load) before any row has ever been
          edited, meaning its very first filter state was unscoped (every
          active product across every Variety) - the same class of mistake
          AD-05 already corrected twice on this screen's List/Scheme filter.
          Replaced with a fresh, already-VarietyCode-scoped DataSource built
          per row directly in onEditorPreparing (or a plain empty array if
          the row has no VarietyCode yet) - there is now no code path that
          can ever request the unscoped set.
    03Sep2026 (SD)
        - #76649: 2116 - UI - Add New Season/Scheme entry fields and START
          duplicate check (Story 3). identityFormItems gains a create-mode
          branch (Season dropdown defaulting to current FY, Scheme free-text
          maxLength 1, both required) bound to a fresh createDraft model,
          plus an inline START button (itemType: "button", mirroring
          gc-popup-save-layout.vue's own inline form buttons - the only
          existing precedent for a button inside a GcForm's items array
          rather than a separate toolbar). onStartClicked locks the entered
          Season+Scheme into selectedRecord/toolbarRecord and reveals the
          (now create-mode-inclusive) price grid, at which point
          identityDataModel/identityFormItems fall back to the same
          read-only display Review/Edit already use.

          No existing GCORPcx screen implements this exact "live fields ->
          lock -> reveal" shape - checked this session against every sibling
          Settings > Intake screen (grower-pool-splits.vue/grower-syndicate-
          splits.vue both use a pick-an-existing-entity popup instead, which
          doesn't apply here since Scheme has no master to pick from at all,
          FLAG-CONCEPT-01). Built directly to prototype.html's own
          showIdentityMode()/startNewRecord() behaviour using only standard
          GcForm items, per /build's own "prototype is a behaviour spec, not
          a pixel-perfect visual one" rule.

          START's own duplicate check: user-stories.md flagged (2026-09-02)
          that the original "check the already-loaded List client-side"
          design assumed the List holds every Scheme+Season, no longer true
          now that Story 1's List is server-paged (AD-05, revised
          2026-09-02). Raised to the developer as a genuine fork -
          client-side-only (re-introduces the stale assumption's exact
          risk), a dedicated scoped existence round-trip (an
          AlmondcoSchemePriceSummary $filter lookup, no new endpoint
          needed), or no check at all (rely purely on Save's existing
          create-mode server-side guard, Task 76645) - developer first
          chose "no check at all", then reversed that same day (see the
          03Sep2026 entry below) in favour of the scoped round-trip after
          all. onStartClicked now calls getSchemePriceSummaryByKey (below)
          before locking anything in.

          A create-mode Save has no RecordId to route to afterwards (unlike
          Edit, where the route's own RecordId is already valid) - resolves
          it via the new getSchemePriceSummaryByKey (service file), a scoped
          $filter lookup on the exact SchemeId+SeasonYear just saved.

        - Row-adding mechanic correction (developer feedback, no numbered
          ADO task - CX convention, not this screen-specific): removed
          DxEditing's native "+" add-row button entirely (allow-adding is
          now unconditionally false) along with onGridToolbarPreparing/
          onInitNewRow, which existed only to support it. Replaced with the
          established CX pattern - a blank row is always auto-present at
          the end of the grid while editing (added on entering Edit mode
          with existing rows, or immediately once Add New's START locks an
          identity in via onStartClicked), and filling in that blank row's
          own Price Type auto-appends a fresh blank row after it
          (ensureTrailingBlankRow, driven by a single deep watch on
          priceRows - see its own comment). A blank row's PriceType is
          `null`, never defaulted to "1" - this is what makes it a genuine,
          unambiguous "not yet filled in" sentinel (this file's own
          Description block previously reasoned no safe sentinel existed
          here, which was the wrong conclusion, not an unavoidable one).
          validateBeforeSave/onSaveClicked both filter out any row still at
          PriceType `null` before validating/submitting, and
          onEditorPreparing's Variety disabled condition was corrected to
          also cover `null` (previously only checked `=== "1"`, which left
          Variety wrongly enabled on a still-blank row).

        - START duplicate check reversed (developer feedback, same day, no
          numbered ADO task): "no check at all" (this file's own #76649
          entry above) is superseded - onStartClicked now runs a scoped
          backend existence check before locking anything in, reusing
          getSchemePriceSummaryByKey (already built for onSaveClicked's own
          post-create routing - see the service file's own comment) rather
          than a new endpoint. A single exact-key $filter round-trip
          (SchemeId+SeasonYear), not the full-list client-side check
          user-stories.md's own (stale) AC describes. If a row is found,
          START blocks with a message identifying the existing Scheme+
          Season rather than locking the fields/revealing the grid. Save's
          existing create-mode server-side guard (Task 76645) still runs
          regardless as the authority - this is a friendlier, earlier
          check, not a replacement for it (a stale check result between
          START and Save - two admins racing, for instance - is still
          caught there).

        - Tabbing sequence (developer feedback, no numbered ADO task): a
          successful START now focuses row 0/column 0 (Price Type)
          automatically (onStartClicked sets a one-shot
          focusFirstCellAfterGridReady flag, consumed by the grid's own next
          content-ready - the grid has not mounted yet at the point
          onStartClicked runs). Tab/Shift+Tab (onGridKeyDown, wired via
          @key-down) now skip any cell that isn't currently editable
          (Variety/Item Code disabled per BR-05, and the delete-buttons
          command column, which was never a data cell to begin with) rather
          than stopping on it the way DevExtreme's own default cell
          navigation does - e.g. a Type 1 row tabs straight from Price Type
          to YTD Price, and a Type 2 row tabs Price Type -> Variety -> YTD
          Price, skipping Item Code both forward and backward. Extracted the
          disabled-condition logic out of onEditorPreparing into a single
          isCellEditable(dataField, rowData) helper shared by both -
          onEditorPreparing (disables the cell's own editor) and
          onGridKeyDown (skips it in the tab sequence) now read from the one
          place, so they cannot drift apart the way two separate copies of
          the same BR-05 condition eventually would.

        - Tabbing sequence, follow-up fix (developer-reported bug, same
          day): the first version above landed on a disabled cell on the
          very first Tab after START's auto-focus regardless, and jumped to
          the wrong row entirely on a later Shift+Tab. Root cause - editCell()
          alone does not reliably keep the widget's own focusedRowIndex/
          focusedColumnIndex options in sync with where it actually moved
          focus to, so onGridKeyDown's next read of those options could be
          stale, computing the wrong "current position" to walk from. Fixed
          by moveFocusToCell (new helper) explicitly setting both options
          *and* calling editCell together, every time focus moves
          programmatically - used both by onGridContentReady's initial
          auto-focus and onGridKeyDown's own skip logic, so neither path can
          drift the tracked position out of sync again.

        - Tabbing sequence, second follow-up fix (developer-reported bug,
          same day, developer's own diagnosis confirmed correct): setting
          Price Type on a blank row then tabbing straight out still skipped
          Variety as if PriceType were still unset - tabbing away and back
          then out again "worked". Root cause: onGridKeyDown's walk read
          this.priceRows[row].PriceType (the plain external array bound as
          the grid's data-source) to decide editability - but that array is
          not necessarily written yet at the exact moment the Tab keydown
          fires, even though the grid's own internal edit state already
          knows the just-picked value (onEditorPreparing's e.row.data
          already reads that internal state correctly, which is why the
          cell's own disabled styling was never wrong - only this handler's
          separate, external read was). Fixed by reading
          grid.cellValue(row, "PriceType") instead - the grid's own live
          accessor for a cell's current value, pending edit or not - rather
          than trusting the external array to already be in sync.

        - Tabbing sequence, third follow-up fix (developer-reported bug,
          same day, saveEditData() attempt below did not actually fix it):
          YTD Price (a typed dxNumberBox, unlike the select-box columns
          whose value already commits on selection, before Tab is ever
          pressed) reset back to 0 on Tab out instead of keeping what was
          typed. First attempt: preventDefault() (needed to suppress
          DevExtreme's own default Tab handling so our skip logic runs
          instead) also suppresses the value-commit that default handling
          would have done - tried grid.saveEditData() first to force that
          commit. Did not work - reported still broken. Actual root cause:
          preventDefault() on the Tab keydown stops the *browser's own*
          native focus-shift, which is exactly what would have blurred the
          NumberBox's real <input> and fired its own onValueChanged/
          setCellValue commit; with that native blur never happening, the
          typed value never left the still-focused editor's own DOM input
          in the first place - there was nothing "pending" in DevExtreme's
          own tracked edit state for saveEditData() to save. Fixed by
          forcing that blur manually instead (document.activeElement.blur()
          + $nextTick before moving focus) - the exact same idiom
          onSaveClicked already uses for the identical reason (flush
          whatever's mid-edit before reading priceRows), reused here rather
          than invented fresh.

        - Footer text removed (developer decision, no numbered ADO task):
          the panel footer's second line, "Full delete-then-insert on Save
          (BR-09)", was carried over verbatim from prototype.html's own
          panel-footer (not one of that file's separate flag-note reviewer
          callouts - it sat in the actual mockup, styled with a real design
          token). Raised to the developer as worth keeping - BR-09's
          full-replace Save has no other safeguard of any kind (no
          confirm dialog, declined earlier this session per
          technical-design.md's own "genuine, disclosed risk" framing) - but
          the developer decided to drop it regardless. Row Count is now the
          footer's only line.

    06Sep2026 (SD)
        - #77386 fix: Item Code field showed only the raw code after
          selection, in every mode - itemCodeCellTemplate had no
          description-resolution path at all (unlike varietyCellTemplate/
          varietyDisplayText, which resolve against the fully preloaded
          varietyOptions master), contradicting test-cases.md TC-026/TC-027
          and technical-design.md's own "coded-lookup" spec for Item Code
          (line 104). Full trace in investigation.md under this project
          folder (Bug #77386) - two independent gaps, both fixed together:
          (1) api.AlmondcoSchemePrice.sql gained a LEFT JOIN to dbo.PRODUCTS
          (ProductDescription, ISNULL-guarded to '' - same join
          AlmondcoIntakeVarietySizeProductView.sql already uses for this
          column's own editor dropdown), mapped by loadPriceRows into each
          row's new ProductDescription field - covers Review mode and any
          already-saved row in Edit mode; (2) onEditorPreparing's ItemCode
          branch gained an onSelectionChanged handler that captures the
          selected item's own ProductDescription onto the row directly (
          setCellValue only ever receives the raw ProductCode/valueExpr, not
          the full item object) - covers a row picked fresh in the current
          Create/Edit session, before Save. itemCodeCellTemplate now calls a
          new itemCodeDisplayText method (mirrors varietyDisplayText's own
          "Code - Description"/bare-code-fallback shape); PriceType's Type-1/
          Type-2 clearing and Variety's own re-scoping setCellValue both
          clear ProductDescription alongside ItemCode, and createBlankPriceRow
          initialises it to '' up front (Vue 2 reactivity - a property can't
          be added reactively after the fact). Deliberately did NOT preload a
          full Item Code master to resolve this client-side instead - that is
          the exact anti-pattern this same file's own History already
          documents fixing twice (itemCodeDataSource's own scoped-DataSource
          correction, 02Sep2026 entries above); the fix here specifically
          avoids reintroducing it.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import { FeatureStorage } from "@/_services/storage";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { store } from "@/_store";
import { AlmondcoSchemePriceService } from "@/_services/api/settings/purchasing/almondco-scheme-price.service";
import { DxDataGrid } from "devextreme-vue/data-grid";

export default {
  mixins: [RouteMixin, FormMixin],
  components: { DxDataGrid },
  data() {
    return {
      ColumnWidth,

      // Season fixed 3-value set (§6) - current FY resolved the same way
      // console-season-year.vue's own setDefaultSeasonYear does.
      seasonOptions: [],
      currentFiscalYear: null,

      // The selected List row's identity (Season/SchemeId), resolved from the
      // route's RecordId via a lookup against AlmondcoSchemePriceSummary
      // (see loadSelectedRecord).
      selectedRecord: {},
      toolbarRecord: {},

      // #76649 (Story 3 - Add New): the live-entry draft identityFormItems'
      // create-mode branch binds to before START locks it in - kept
      // separate from selectedRecord so the read-only Review/Edit branch's
      // own model is never touched by a still-in-progress Add New entry.
      // SeasonYear defaults to the current fiscal year (FLAG-CONCEPT-03,
      // filled in by the currentFiscalYear watcher/the baseView.mode watcher
      // below once loadSeasonOptions resolves it).
      createDraft: { SeasonYear: null, SchemeId: "" },

      // True once START has accepted a Season+Scheme pair for this Add New
      // session - reveals the price-row grid and switches identityFormItems/
      // identityDataModel back to the same read-only display Review/Edit use.
      createIdentityLocked: false,

      // One-shot flag: set by onStartClicked right before the grid mounts
      // for the first time (v-if flips true), consumed by the very next
      // onGridContentReady to focus row 0/column 0 (Price Type) - never set
      // again afterwards, so later content-ready events (an edit, a row
      // delete, the auto-added blank row) don't keep stealing focus back to
      // the top of the grid.
      focusFirstCellAfterGridReady: false,

      // Local, mutable array bound directly to DxDataGrid's data-source -
      // Add/Remove/edit all operate on this array in place via DxEditing's
      // native allow-adding/allow-deleting/allow-updating, same idiom as the
      // sibling grower-pool-splits.vue's own splitRows.
      priceRows: [],
      nextRowKey: 1,
      rightGridHeight: "auto",
      loaded: false,

      // §6/BR-05: Price Type option list - fixed, not a lookup table.
      priceTypeOptions: [
        { Code: "1", Description: "1 - Default" },
        { Code: "2", Description: "2 - Variety" },
        { Code: "3", Description: "3 - Variety + Size" },
      ],

      // §6/BR-06: Variety option list - AlmondcoIntakeVariety, Inactive eq
      // 'N', loaded once (small reference master, see loadVarietyOptions).
      varietyOptions: [],

      // §6/BR-06: Item Code has no cached option list here at all -
      // deliberately, per developer feedback this session: a shared
      // DataSource kept in data() would have to start out unscoped (every
      // active product, every Variety) before any row's own filter could
      // ever be applied to it, repeating the exact unbounded-fetch mistake
      // AD-05 already corrected twice. onEditorPreparing instead builds a
      // fresh, already-VarietyCode-scoped DataSource per row, on demand, each
      // time that row's own Item Code editor is about to open - see the
      // service's own itemCodeDataSource comment.
    };
  },

  // Matches the sibling grower-pool-splits.vue/grower-syndicate-splits.vue's
  // own beforeRouteEnter - awaiting the dispatch before calling next() blocks
  // the route transition until CurrentFiscalYear is actually resolved.
  async beforeRouteEnter(to, from, next) {
    await store.dispatch("defaultDateSettings/setCurrentFiscalData");
    next();
  },

  computed: {
    showRightPanel() {
      return (
        (this.baseView.id !== null &&
          this.baseView.id !== undefined &&
          this.baseView.id !== "") ||
        this.baseView.mode === "create"
      );
    },

    getHeight() {
      return this.rightGridHeight;
    },

    // §6: "+ ADD NEW" opens directly in Edit mode - matching the sibling
    // screens' own isEditing shape. Still true for the whole create-mode
    // session (before and after START - see createIdentityLocked), matching
    // prototype.html's own `editMode`/`creating` flags never diverging.
    isEditing() {
      return this.baseView.mode === "edit" || this.baseView.mode === "create";
    },

    // §6: no Active/Inactive toggle for this screen. save is gated on the
    // create-vs-update ternary technical-design.md §6 specifies (project-wide
    // PO ruling 2026-08-25, corrected in the 2026-08-25 revision) - not
    // 'create' in both modes.
    // #76649 addition: in create mode, save also stays hidden until START
    // has actually locked a Season+Scheme identity in (createIdentityLocked)
    // - matches prototype.html's own `saveBtn.disabled = true` until
    // startNewRecord() succeeds; clicking Save before then would otherwise
    // only be caught after the fact by validateBeforeSave's own
    // "Select a Scheme+Season before saving" message.
    rightToolbarOptions() {
      return {
        closeSplit: true,
        edit: FeatureStorage.checkPermission(
          "AlmondcoSchemePrices",
          "create",
        ),
        save:
          FeatureStorage.checkPermission("AlmondcoSchemePrices", "create") &&
          (this.baseView.mode !== "create" || this.createIdentityLocked),
        audit: this.baseView.mode === "review",
      };
    },

    // #76649: which model identityFormItems' fields read/write - the
    // in-progress createDraft while Add New's identity is still being
    // entered, or the real selectedRecord everywhere else (Review/Edit, and
    // create mode once START has locked an identity in and copied it into
    // selectedRecord - see onStartClicked).
    identityDataModel() {
      return this.baseView.mode === "create" && !this.createIdentityLocked
        ? this.createDraft
        : this.selectedRecord;
    },

    // #76649: a touch taller while create mode's 3-item entry form (Season +
    // Scheme + START button) is showing, vs. the plain 2-field read-only
    // display everywhere else.
    identityFormHeight() {
      return this.baseView.mode === "create" && !this.createIdentityLocked
        ? 170
        : 110;
    },

    // Identity row (§6): Season/Scheme. Read-only, resolved from the route's
    // RecordId lookup in Review/Edit and never separately re-entered there
    // (BR-04). In create mode before START, this instead renders Story 3's
    // own live entry fields ("2116 - UI - Add New Season/Scheme entry fields
    // and START duplicate check") - once START locks an identity in
    // (createIdentityLocked), it falls through to the same read-only display
    // as Review/Edit, since identityDataModel now points at selectedRecord
    // too.
    //
    // No existing GCORPcx screen implements this exact "live fields -> lock
    // -> reveal" shape (checked this session against every sibling Settings >
    // Intake screen) - grower-pool-splits.vue/grower-syndicate-splits.vue
    // both use a pick-an-existing-entity popup instead, which doesn't apply
    // here since Scheme has no master to pick from at all (FLAG-CONCEPT-01).
    // Built directly to prototype.html's own showIdentityMode()/
    // startNewRecord() behaviour (a behaviour/content spec, not a
    // pixel-perfect visual one, per /build's own component-reuse rule) using
    // only standard GcForm items - the itemType: "button" shape mirrors
    // gc-popup-save-layout.vue's own inline form buttons, the only existing
    // precedent in this codebase for a button living inside a GcForm's items
    // array rather than a separate toolbar.
    identityFormItems() {
      if (this.baseView.mode === "create" && !this.createIdentityLocked) {
        return [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "SeasonYear",
                label: { text: "Season" },
                editorType: "dxSelectBox",
                required: true,
                editorOptions: {
                  dataSource: this.seasonFilterOptions,
                  valueExpr: "code",
                  displayExpr: "code",
                  searchEnabled: false,
                  onValueChanged: (e) => {
                    this.createDraft.SeasonYear = e.value;
                  },
                },
              },
              {
                dataField: "SchemeId",
                label: { text: "Scheme" },
                editorType: "dxTextBox",
                required: true,
                editorOptions: {
                  maxLength: 1,
                  // Forces uppercase as-typed, matching prototype.html's own
                  // onPickerSchemeInput - SchemeId is a bare, case-sensitive
                  // free-text code (FLAG-CONCEPT-01) with no master to
                  // normalise it otherwise.
                  onValueChanged: (e) => {
                    const upper = (e.value || "").toUpperCase();
                    this.createDraft.SchemeId = upper;
                    if (e.value !== upper) e.component.option("value", upper);
                  },
                },
              },
              {
                itemType: "group",
                colCount: 1,
                cssClass: "scheme-price-start-group",
                items: [
                  {
                    itemType: "button",
                    horizontalAlignment: "left",
                    buttonOptions: {
                      text: "START",
                      type: "default",
                      width: 90,
                      useSubmitBehavior: false,
                      onClick: () => this.onStartClicked(),
                    },
                  },
                ],
              },
            ],
          },
        ];
      }

      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "SeasonYear",
              label: { text: "Season" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
            {
              dataField: "SchemeId",
              label: { text: "Scheme" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
          ],
        },
      ];
    },

    // §6: Price Type governs Variety/Item Code (BR-05) - Variety enabled/
    // required for Type 2/3 only, Item Code for Type 3 only (onEditorPreparing
    // below applies the per-row disabled state; setCellValue clears whatever
    // no longer applies when Price Type changes).
    getEditableColumns() {
      return [
        {
          dataField: "PriceType",
          caption: "Price Type",
          minWidth: 170,
          editorType: "dxSelectBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            dataSource: this.priceTypeOptions,
            valueExpr: "Code",
            displayExpr: "Description",
            searchEnabled: true,
          },
          cellTemplate: "priceTypeCellTemplate",
          setCellValue: (rowData, value) => {
            rowData.PriceType = value;
            if (value === "1") {
              rowData.VarietyCode = "";
              rowData.ItemCode = "";
              rowData.ProductDescription = "";
            } else if (value === "2") {
              rowData.ItemCode = "";
              rowData.ProductDescription = "";
            }
          },
        },
        {
          dataField: "VarietyCode",
          caption: "Variety",
          minWidth: 170,
          editorType: "dxSelectBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            dataSource: this.varietyOptions,
            valueExpr: "Code",
            displayExpr: this.varietyDisplayExpr,
            searchEnabled: true,
            searchExpr: ["Code", "Name"],
            showClearButton: true,
          },
          cellTemplate: "varietyCellTemplate",
          setCellValue: (rowData, value) => {
            rowData.VarietyCode = value;
            // BR-06: Item Code re-scopes to the newly selected Variety.
            rowData.ItemCode = "";
            rowData.ProductDescription = "";
          },
        },
        {
          // #77386 fix (06Sep2026): a already-saved row's own description
          // now travels with it from the server - api.AlmondcoSchemePrice.sql
          // gained a LEFT JOIN to dbo.PRODUCTS (see that file's own comment),
          // mapped into ProductDescription by loadPriceRows below - so
          // itemCodeDisplayText (this column's own cellTemplate) can resolve
          // it the same way varietyDisplayText does, without needing a full
          // preloaded master. For a row picked in the *current* session
          // (before Save), onEditorPreparing's onSelectionChanged captures
          // the same field directly off the selected item - see its own
          // comment. Previously this column had no description-resolution
          // path at all (bare code only, in every mode) - see investigation.md
          // under projects/Almondco - 2116 - Intake Masters Migration/
          // Almondco - 2116 - Scheme Prices Master/ (Bug #77386) for the full
          // trace.
          //
          // No dataSource here - a shared, column-level DataSource would have
          // to start unscoped (every active product, every Variety) before
          // the first row-specific filter could ever be applied, which is
          // exactly the unbounded fetch this column exists to avoid. Instead
          // onEditorPreparing below builds a fresh, already-VarietyCode-
          // scoped DataSource per row, right before that row's own editor
          // opens - there is never a moment where an unscoped query can fire.
          dataField: "ItemCode",
          caption: "Item Code",
          minWidth: 170,
          editorType: "dxSelectBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            valueExpr: "ProductCode",
            displayExpr: this.itemCodeDisplayExpr,
            searchEnabled: true,
            searchExpr: ["ProductCode", "ProductDescription"],
            showClearButton: true,
          },
          cellTemplate: "itemCodeCellTemplate",
          setCellValue: (rowData, value) => {
            rowData.ItemCode = value;
          },
        },
        {
          dataField: "YtdPrice",
          caption: "YTD Price",
          minWidth: 110,
          alignment: "right",
          editorType: "dxNumberBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-numberbox-field gc-editable-cell-color"
              : undefined,
          format: "#,##0.0000",
          editorOptions: {
            min: 0,
            showSpinButtons: false,
            format: "#,##0.0000",
          },
          setCellValue: (rowData, value) => {
            rowData.YtdPrice = value;

            return rowData;
          },
        },
        {
          visible: this.baseView.mode != "review",
          caption: "",
          dataField: "",
          type: "buttons",
          alignment: "right",
          maxWidth: 50,
          width: 50,
          buttons: ["delete"],
        },
      ];
    },

    // The footer's own Row Count - excludes the always-present trailing
    // blank row (PriceType null - see ensureTrailingBlankRow) so the count
    // shown matches what Save will actually submit, not priceRows.length.
    realPriceRowCount() {
      return this.priceRows.filter((r) => r.PriceType).length;
    },

    // Bug #77841: RowKeys of every row sharing a Price Type+Variety+Item Code
    // combination with at least one other row - drives onPriceRowPrepared's
    // grid highlight, giving a visual cue before Save is even attempted
    // rather than only a post-Save alert (see this bug's Expected Outcome).
    duplicatePriceRowKeys() {
      const keys = new Set();
      this.getDuplicatePriceRowGroups().forEach((group) =>
        group.rows.forEach((row) => keys.add(row.RowKey)),
      );
      return keys;
    },

    // §6: fixed 3-value set (current FY-1/FY/FY+1) - not the full
    // AlmondcoIntakeSeason master list the sibling split screens' own Season
    // filter draws from.
    seasonFilterOptions() {
      if (!this.currentFiscalYear) return [];
      return [
        this.currentFiscalYear - 1,
        this.currentFiscalYear,
        this.currentFiscalYear + 1,
      ].map((year) => ({ code: year }));
    },

    leftGridConfig() {
      return {
        title: "Scheme Prices",
        pageRoute: "/scheme-prices",
        exportFileName: "Scheme Prices List",

        gridData: {
          gridOptions: {
            // Server-driven, matching grower-syndicate-splits.vue's own
            // AlmondcoGrowerSyndicateSplitSummary binding exactly - GcDataGrid
            // queries this endpoint directly (OData $filter/$orderby/paging),
            // with Season/Scheme filter panel selections applied server-side
            // since both are now real columns on the summary view. No
            // client-side grouping of raw rows happens here at all - the
            // fix for the data-volume concern the original AD-05 approach
            // had (see this file's own header comment).
            dataSource: {
              endpoint: "AlmondcoSchemePriceSummary",
              serverPaging: true,
              allowGrouping: false,
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: () => {
                  this.addNewSchemePrice();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoSchemePrices",
                  "create",
                ),
              },
            ],
          },

          columns: [
            {
              // §6: Season filter is a fixed 3-value set (current FY-1/FY/
              // FY+1), not the AlmondcoIntakeSeason-backed screenField the
              // sibling split screens' own Season filter uses - see this
              // file's own header comment. "ColumnValue" satisfies gc-form's
              // screenField requirement for filter fields only, same trick as
              // bin-comments.vue's own Site column.
              screenField: "AlmondcoIntakeSeason",
              gridDataField: "SeasonYear",
              filterDataField: "SeasonYear",
              label: "Season",
              filterLabel: "Season",
              filterIndex: 0,
              type: "Integer",
              valueExpr: "Season",
              displayExpr: "SeasonAsString",
              searchExpr: "SeasonAsString",
              allowHiding: false,
              width: ColumnWidth.SeasonYear.width + 30,
              minWidth: ColumnWidth.SeasonYear.minWidth + 30,
              forceWidth: true,
              sortIndex: 0,
              sortOrder: "desc",
            },
            {
              // Field mapping (user-stories.md Story 1): SchemeId -> "Scheme",
              // bare code only - no description exists anywhere to show
              // alongside it (FLAG-CONCEPT-01, see this file's own header
              // comment re: TC-006). Filter options are the distinct codes
              // already in use, sourced from a dedicated small lookup
              // endpoint (api/AlmondcoSchemeSearch - a plain SELECT DISTINCT
              // over api.AlmondcoSchemePrice) rather than fetched-and-
              // deduplicated client-side from the growing Summary endpoint -
              // developer-flagged scaling concern, same class of fix as
              // grower-pool-splits.vue's own AlmondcoGrowerPoolSearch
              // (#75930). GcDataGrid queries this endpoint directly (remote
              // DataSource, standard DevExtreme paging) each time the filter
              // dropdown opens - no upfront full fetch on screen load at all.
              screenField: "ColumnValue",
              gridDataField: "SchemeId",
              filterDataField: "SchemeId",
              label: "Scheme",
              filterLabel: "Scheme",
              filterIndex: 1,
              type: "String",
              dataSourceConfig: {
                endpoint: "api/AlmondcoSchemeSearch",
                sort: { selector: "SchemeId", asc: true },
              },
              valueExpr: "SchemeId",
              displayExpr: "SchemeId",
              allowHiding: false,
              width: ColumnWidth.ColumnValue.width,
              minWidth: ColumnWidth.ColumnValue.minWidth,
            },
            {
              // Server-supplied - COUNT of AlmondcoSchemePrice rows sharing
              // this Season+Scheme, computed in AlmondcoSchemePriceSummary.sql.
              dataField: "PriceRows",
              label: "Price Rows",
              alignment: "right",
              allowHiding: false,
              width: ColumnWidth.ItemCount.width,
              minWidth: ColumnWidth.ItemCount.minWidth,
              forceWidth: true,
            },
            {
              // Server-supplied - ChangedBy/DateChanged of the single
              // underlying price row with this Season+Scheme's
              // MAX(DateChanged), computed in AlmondcoSchemePriceSummary.sql
              // (added 02Sep2026, developer direction). Rendered as one
              // combined "{ChangedBy} ({DateChanged})" cell per the
              // prototype (e.g. "AEbert (15 Jul 2026)") - toShortDateString()
              // is this app's own established date-display format
              // (prototype-date.js, globally available on Date instances),
              // reused rather than a fresh formatting convention.
              screenField: "DateChanged",
              dataField: "DateChanged",
              label: "Last Changed",
              calculateCellValue: (rowData) => {
                if (!rowData.DateChanged) return "";
                const formattedDate = new Date(
                  rowData.DateChanged,
                ).toShortDateString();
                return rowData.ChangedBy
                  ? `${rowData.ChangedBy} (${formattedDate})`
                  : formattedDate;
              },
              allowHiding: false,
              // Bug #77845: this cell renders the combined "{ChangedBy}
              // ({DateChanged})" string (e.g. "GRAYCORP (10 Sep 2026)"), not
              // a bare date - ColumnWidth.Date (100/100) was sized for the
              // latter and wrapped the former onto two lines despite the
              // List having plenty of spare horizontal room. Widened to fit
              // that combined text on one line, and wordWrapEnabled
              // (gc-data-grid.vue's new per-column opt-out) turned off so an
              // unusually long ChangedBy value truncates with an ellipsis
              // instead of wrapping again.
              width: 190,
              minWidth: 160,
              wordWrapEnabled: false,
              forceWidth: true,
              allowSearch: false,
            },
          ],
        },
      };
    },
  },

  watch: {
    // Drives both the row-click flow and a direct/bookmarked URL identically -
    // baseView.id is route-derived (RouteMixin), so this fires either way.
    "baseView.id": {
      immediate: true,
      handler(routeId) {
        if (routeId) {
          this.loadSelectedRecord(routeId);
        } else {
          this.selectedRecord = {};
          this.toolbarRecord = {};
          this.priceRows = [];
        }
        this.setRightGridHeight();
      },
    },
    "baseView.mode": {
      immediate: true,
      handler(mode) {
        this.setRightGridHeight();

        // Resets state so a fresh /create visit never shows a stale
        // previously-selected record, and re-arms Story 3's own Season/
        // Scheme entry + START mechanic. currentFiscalYear may still be null
        // here if this fires before loadSeasonOptions (created()) resolves -
        // the currentFiscalYear watcher below fills SeasonYear in once it
        // does.
        if (mode === "create") {
          this.selectedRecord = {};
          this.toolbarRecord = {};
          this.priceRows = [];
          this.createIdentityLocked = false;
          this.createDraft = {
            SeasonYear: this.currentFiscalYear,
            SchemeId: "",
          };
        } else if (mode === "edit") {
          // Review -> Edit does not itself change baseView.id, so
          // loadPriceRows never reruns here - this is the one place that
          // needs to explicitly re-check the trailing-blank-row invariant
          // for that transition (the priceRows deep watcher below only
          // fires on an actual priceRows mutation, which this isn't).
          this.ensureTrailingBlankRow();
        }
      },
    },

    // CX convention (developer feedback, 2026-09-03): keeps exactly one
    // blank row (PriceType `null`) at the end of the grid at all times while
    // editing - fires after every in-place priceRows mutation, so filling in
    // the current last row's own Price Type (which is what actually commits
    // that mutation - see getEditableColumns' own setCellValue) auto-appends
    // the next blank row, and deleting every row down to none self-heals
    // back to a single blank row rather than leaving the grid impossible to
    // add to (there is no manual "Add Row" button to fall back on).
    priceRows: {
      deep: true,
      handler() {
        this.ensureTrailingBlankRow();

        // Bug #77841: repaint-changes-only (see the grid's own template
        // attribute) only re-fires row-prepared for the row whose data
        // actually changed - editing row A to no longer collide with row B
        // would otherwise leave row B's now-stale duplicate highlight in
        // place, since B's own data never changed. A full repaint re-runs
        // onPriceRowPrepared against every row so the highlight always
        // matches the current duplicatePriceRowKeys - cheap here since this
        // grid is never paged/virtualised (a Scheme+Season's own line count).
        this.$nextTick(() => {
          const grid = this.$refs.rightGrid;
          if (grid && grid.instance) {
            grid.instance.repaint();
          }
        });
      },
    },

    // #76649: backfills createDraft's Season default (FLAG-CONCEPT-03) for
    // the case where a /create visit's mode watcher (immediate, runs during
    // component init) fires before created()'s awaited loadSeasonOptions has
    // resolved currentFiscalYear - without this, a direct/bookmarked visit
    // to /create would show the Season dropdown with no default selected.
    currentFiscalYear(val) {
      if (
        this.baseView.mode === "create" &&
        !this.createIdentityLocked &&
        !this.createDraft.SeasonYear
      ) {
        this.createDraft.SeasonYear = val;
      }
    },
  },

  async created() {
    this.showLoading(true);
    // #76649: now actually needed - Story 3's Season dropdown/default reads
    // currentFiscalYear, which this populates (previously dead code, left
    // commented out ahead of this task per this file's own original
    // Story-1-era comment).
    await this.loadSeasonOptions();
    this.loadVarietyOptions();

    this.$nextTick(() => {
      this.showLoading(false);
      this.loaded = true;
    });
  },

  methods: {
    backButtonFunction() {
      this.$router.push({ path: "/settings/intake" });
    },

    onLeftRowClick(e) {
      if (!e.data) return;
      this.onRowClicked(e, {
        page: "scheme-prices",
        mode: "review",
        idProperty: "RecordId",
      });
    },

    // §6: "+ ADD NEW" opens the detail panel directly in Edit mode via the
    // Season+Scheme entry fields (Story 3) - wiring the route here only,
    // matching the sibling grower-syndicate-splits.vue's own
    // addNewGrowerSyndicateSplit precedent.
    addNewSchemePrice() {
      this.$router.push({ path: `${this.leftGridConfig.pageRoute}/create` });
    },

    // §6/BR-03: Season option list/current-FY resolution - Season/StartDate/
    // EndDate from AlmondcoIntakeSeason, "current FY" resolved the same way
    // console-season-year.vue's own setDefaultSeasonYear does (the row whose
    // date range contains today). Only the current FY value is actually
    // needed here (seasonFilterOptions computes the fixed 3-value set from
    // it) - the full row set is kept only for parity with the sibling's own
    // method shape.
    async loadSeasonOptions() {
      try {
        const res = await AlmondcoSchemePriceService.getIntakeSeasons();
        const rows = (res.data && res.data.value) || [];
        this.seasonOptions = rows;
        const today = new Date();
        const currentSeason = rows.find(
          (s) => new Date(s.StartDate) <= today && today <= new Date(s.EndDate),
        );
        this.currentFiscalYear = currentSeason ? currentSeason.Season : null;
      } catch (err) {
        this.seasonOptions = [];
        this.currentFiscalYear = null;
      }
    },

    // §5: AlmondcoSchemePriceSummary, filtered to a single row's RecordId -
    // resolves the identity row (SchemeId/SeasonYear) on baseView.id change,
    // whether that's a row click or a direct/bookmarked URL - matching
    // grower-syndicate-splits.vue's own loadSelectedRecord. toolbarRecord is
    // set from the full summary row (not the narrowed selectedRecord below) -
    // AlmondcoSchemePriceSummary already carries the full audit block
    // (CreatedBy/DateCreated/ChangedBy/DateChanged, added earlier this
    // session), which the toolbar's Audit popup reads off toolbarRecord.
    async loadSelectedRecord(recordId) {
      this.showLoading(true);
      try {
        const res = await AlmondcoSchemePriceService.getSchemePriceSummary(
          recordId,
        );
        const rows = (res.data && res.data.value) || [];
        const summary = rows[0] || {};
        this.selectedRecord = summary.SchemeId
          ? { SchemeId: summary.SchemeId, SeasonYear: summary.SeasonYear }
          : {};
        this.toolbarRecord = summary.SchemeId ? summary : {};
        if (summary.SchemeId) {
          await this.loadPriceRows(summary.SchemeId, summary.SeasonYear);
        } else {
          this.priceRows = [];
        }
      } catch (err) {
        this.selectedRecord = {};
        this.toolbarRecord = {};
        this.priceRows = [];
      } finally {
        this.showLoading(false);
      }
    },

    // §5: existing price rows for the selected Scheme+Season, the raw
    // api.AlmondcoSchemePrice shim (unchanged, still the detail panel's own
    // row-level load per technical-design.md §5/§8). Mapped into the grid's
    // own local RowKey shape.
    async loadPriceRows(schemeId, seasonYear) {
      try {
        const res = await AlmondcoSchemePriceService.getSchemePriceSet(
          schemeId,
          seasonYear,
        );
        const rows = (res.data && res.data.value) || [];
        this.priceRows = rows.map((r) => ({
          RowKey: this.nextRowKey++,
          PriceType: r.PriceType,
          VarietyCode: r.VarietyCode,
          ItemCode: r.ItemCode,
          // #77386 fix: api.AlmondcoSchemePrice now joins to dbo.PRODUCTS for
          // this - see that view's own comment. Always a string ('' when
          // ItemCode itself is blank, or has no matching PRODUCTS row), never
          // undefined - matters for Vue 2 reactivity (see createBlankPriceRow's
          // own comment on why every field must be initialised up front).
          ProductDescription: r.ProductDescription,
          YtdPrice: r.YtdPrice,
        }));
      } catch (err) {
        this.priceRows = [];
      }
    },

    // §6/BR-06: Variety option list, loaded once - Inactive eq 'N' only
    // (developer direction this session, see the service's own comment).
    async loadVarietyOptions() {
      try {
        const res = await AlmondcoSchemePriceService.getVarieties();
        this.varietyOptions = (res.data && res.data.value) || [];
      } catch (err) {
        this.varietyOptions = [];
      }
    },

    varietyDisplayExpr(item) {
      return item ? `${item.Code} - ${item.Name}` : "";
    },
    varietyDisplayText(code) {
      if (!code) return "";
      const opt = this.varietyOptions.find((v) => v.Code === code);
      return opt ? `${opt.Code} - ${opt.Name}` : code;
    },
    itemCodeDisplayExpr(item) {
      return item ? `${item.ProductCode} - ${item.ProductDescription}` : "";
    },
    // #77386 fix: mirrors varietyDisplayText's own "Code - Description" shape
    // and its own bare-code fallback. Unlike Variety, there is no full
    // preloaded master to resolve a bare code against here (BR-06 scaling
    // discipline - see itemCodeDataSource's own comment) - ProductDescription
    // instead travels with the row itself: from the server for an
    // already-saved row (loadPriceRows, api.AlmondcoSchemePrice's own new
    // join), or captured at selection time for a row picked in the current
    // session (onEditorPreparing's onSelectionChanged).
    itemCodeDisplayText(row) {
      if (!row || !row.ItemCode) return "";
      return row.ProductDescription
        ? `${row.ItemCode} - ${row.ProductDescription}`
        : row.ItemCode;
    },
    priceTypeDisplayText(code) {
      const opt = this.priceTypeOptions.find((o) => o.Code === code);
      return opt ? opt.Description : code || "";
    },

    // Story 3 ("2116 - UI - Add New Season/Scheme entry fields and START
    // duplicate check"): locks the freshly-entered Season+Scheme in as this
    // Add New session's identity and reveals the (empty) price-row grid,
    // handing off to Story 2's grid/Save mechanics exactly as
    // user-stories.md's own AC describes.
    //
    // Runs a scoped backend existence check first (developer decision,
    // reversed 2026-09-03 from an earlier "no check at all" call - see this
    // file's own History block) - reuses getSchemePriceSummaryByKey
    // (already built for onSaveClicked's own post-create routing, see its
    // own comment in the service file) rather than a new endpoint: a
    // single, exact-key $filter round-trip on the entered SchemeId+
    // SeasonYear, not the full-list client-side check user-stories.md's own
    // (stale) AC describes - the List is server-paged (AD-05, revised
    // 2026-09-02) and can no longer be trusted to hold every existing
    // Scheme+Season client-side. If a row already exists, START blocks with
    // a message identifying it rather than locking the fields in. Save's
    // own create-mode server-side guard (Task 76645,
    // AlmondcoSchemePriceBusinessRulesValidator) still runs regardless as
    // the authority - this is a friendlier, earlier check, not a
    // replacement for it (e.g. two admins racing between START and Save is
    // still caught there).
    async onStartClicked() {
      const schemeId = (this.createDraft.SchemeId || "").trim().toUpperCase();
      const seasonYear = this.createDraft.SeasonYear;

      if (!seasonYear) {
        await this.$alert("Season is required.", "Unable to Start");
        return;
      }
      if (!schemeId) {
        await this.$alert("Scheme is required.", "Unable to Start");
        return;
      }

      this.showLoading(true);
      try {
        const res = await AlmondcoSchemePriceService.getSchemePriceSummaryByKey(
          schemeId,
          seasonYear,
        );
        const rows = (res.data && res.data.value) || [];
        if (rows.length > 0) {
          await this.$alert(
            `Scheme [${schemeId}], Season [${seasonYear}] already has a price set - open it from the List instead.`,
            "Validation Error",
          );
          return;
        }
      } catch (err) {
        await this.$alert(
          "Unable to check for an existing price set. Please try again.",
          "Validation Error",
        );
        return;
      } finally {
        this.showLoading(false);
      }

      this.selectedRecord = { SchemeId: schemeId, SeasonYear: seasonYear };
      this.toolbarRecord = { ...this.selectedRecord };
      this.priceRows = [];
      this.createIdentityLocked = true;
      // CX convention: the grid never starts genuinely empty while editing -
      // ensureTrailingBlankRow adds Add New's first blank row immediately.
      this.ensureTrailingBlankRow();
      // Developer feedback (2026-09-03): row 0/column 0 (Price Type) should
      // be focused as soon as the grid appears - consumed by the grid's own
      // next content-ready event (see onGridContentReady), since the grid
      // has not mounted yet at this exact point (v-if is only about to flip
      // true via createIdentityLocked above).
      this.focusFirstCellAfterGridReady = true;
      this.setRightGridHeight();
    },

    // A fresh blank row - PriceType `null` (never defaulted), which is what
    // makes it an unambiguous "not yet filled in" sentinel; every other
    // field starts empty/zero the same way onInitNewRow used to before rows
    // were auto-added instead of manually inserted.
    createBlankPriceRow() {
      return {
        RowKey: this.nextRowKey++,
        PriceType: null,
        VarietyCode: "",
        ItemCode: "",
        // #77386 fix: initialised up front like every other field here -
        // Vue 2's reactivity can't track a property added to an object later
        // (e.g. only when onEditorPreparing's onSelectionChanged first sets
        // it), so it must already exist, even blank, from row creation.
        ProductDescription: "",
        YtdPrice: 0,
      };
    },

    // CX convention (developer feedback, 2026-09-03, no numbered ADO task):
    // no manual "Add Row" button anywhere - a blank row is always kept
    // auto-present at the end of the grid while editing instead. Called from
    // three places, all funnelling through this one invariant check rather
    // than duplicating it: the priceRows deep watcher below (fires after
    // every in-place mutation - covers a Price Type just being filled in on
    // the current last row, and the edge case of every row being deleted
    // down to none), the baseView.mode watcher's "edit" branch (covers a
    // Review -> Edit transition, which does NOT itself mutate priceRows -
    // loadPriceRows already ran against baseView.id, which didn't change),
    // and onStartClicked (Add New's own first blank row). Idempotent - does
    // nothing once the last row is already blank, so the deep watcher
    // re-firing after its own push does not loop.
    ensureTrailingBlankRow() {
      if (!this.isEditing) return;
      const rows = this.priceRows;
      const lastRow = rows[rows.length - 1];
      if (!lastRow || lastRow.PriceType) {
        this.priceRows.push(this.createBlankPriceRow());
      }
    },

    // BR-05: whether a given column is actually enterable on a given row -
    // Variety for Type 2/3 only, Item Code for Type 3 only, PriceType/
    // YtdPrice always, the delete-buttons column (empty dataField) never.
    // The single source of truth for "editable" behind BOTH onEditorPreparing
    // (disables the cell's own editor) and onGridKeyDown (Tab/Shift+Tab
    // skips it) - kept as one shared check so the two can never disagree
    // about what's actually enterable on a row.
    isCellEditable(dataField, rowData) {
      if (dataField === "VarietyCode") {
        return rowData.PriceType === "2" || rowData.PriceType === "3";
      }
      if (dataField === "ItemCode") {
        return rowData.PriceType === "3";
      }
      return !!dataField;
    },

    // BR-06: a fresh DataSource is built here, per row, already filtered to
    // that row's own selected VarietyCode - never a shared/reused instance
    // that could start out unscoped (developer-flagged scaling concern: a
    // column-level dataSource has to exist before any row is ever edited,
    // which means it would have to start unfiltered - every active product,
    // every Variety - repeating the exact mistake AD-05 already corrected
    // twice this session). If the row has no VarietyCode yet, the option
    // list is a plain empty array - no server call at all, not a fallback to
    // "every active product everywhere".
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;

      if (e.dataField === "VarietyCode") {
        e.editorOptions.disabled = !this.isCellEditable(
          "VarietyCode",
          e.row.data,
        );
      }

      if (e.dataField === "ItemCode") {
        e.editorOptions.disabled = !this.isCellEditable("ItemCode", e.row.data);
        e.editorOptions.dataSource = e.row.data.VarietyCode
          ? AlmondcoSchemePriceService.itemCodeDataSource(
              e.row.data.VarietyCode,
            )
          : [];

        // #77386 fix: captures the selected item's own ProductDescription
        // onto the row at selection time - setCellValue (getEditableColumns'
        // own ItemCode column) only ever receives the raw ProductCode
        // (valueExpr), never the full item object, so this is the one place
        // that can access it. Also fires on DevExtreme's own initial resolve
        // when an already-set ItemCode value matches an item in this row's
        // freshly-scoped DataSource (e.g. re-opening an existing row's editor
        // without changing it) - a bonus early-populate, not required for
        // correctness since api.AlmondcoSchemePrice.sql's own new join
        // already covers that case from the server side (see loadPriceRows).
        e.editorOptions.onSelectionChanged = (args) => {
          e.row.data.ProductDescription = args.selectedItem
            ? args.selectedItem.ProductDescription
            : "";
        };
      }
    },

    // Fixes a bug reported 2026-09-03: editCell() alone does not reliably
    // keep the widget's own focusedRowIndex/focusedColumnIndex options in
    // sync with where it actually moved focus to - so a later Tab press
    // reading those options back (onGridKeyDown below) could see a stale
    // position, computing the wrong next cell or falling back to
    // DevExtreme's own default navigation entirely (symptoms: the very
    // first Tab after START's auto-focus landing on a disabled cell anyway,
    // and Shift+Tab from a later row's last column jumping to the wrong
    // row). Explicitly setting both options *and* calling editCell,
    // together, every single time focus moves programmatically (both here
    // and in onGridKeyDown's own skip logic), is what keeps the next read
    // of those options accurate - never rely on editCell to do that on its
    // own.
    moveFocusToCell(grid, rowIndex, columnIndex) {
      grid.option("focusedRowIndex", rowIndex);
      grid.option("focusedColumnIndex", columnIndex);
      grid.editCell(rowIndex, columnIndex);
    },

    // Developer feedback (2026-09-03): Tab/Shift+Tab must skip any cell
    // that isn't currently editable (Variety/Item Code disabled by Price
    // Type - BR-05 - and the delete-buttons command column, which is never
    // a data cell) rather than stopping on it the way DevExtreme's own
    // default cell navigation does. Walks forward/backward through
    // (row, column) pairs - wrapping columns within a row into the next/
    // previous row - until landing on a cell isCellEditable agrees is
    // enterable, then moves there via moveFocusToCell (see its own comment
    // for why that helper, not a bare editCell call, is required). Falls
    // through to DevExtreme's own default Tab handling at the very
    // start/end of the grid (nowhere left to land) rather than looping or
    // trapping focus.
    onGridKeyDown(e) {
      if (!this.isEditing) return;
      const keyEvent = e.event;
      if (!keyEvent || keyEvent.key !== "Tab") return;

      const grid = e.component;
      const startRow = grid.option("focusedRowIndex");
      const startColumn = grid.option("focusedColumnIndex");
      if (
        startRow == null ||
        startRow < 0 ||
        startColumn == null ||
        startColumn < 0
      ) {
        return;
      }

      const columns = grid.getVisibleColumns();
      const rowCount = this.priceRows.length;
      const forward = !keyEvent.shiftKey;

      let row = startRow;
      let col = startColumn;
      let target = null;

      for (let steps = 0; steps < rowCount * columns.length; steps++) {
        col += forward ? 1 : -1;
        if (col >= columns.length) {
          col = 0;
          row += 1;
        } else if (col < 0) {
          col = columns.length - 1;
          row -= 1;
        }
        if (row < 0 || row >= rowCount) break;

        // Reads PriceType via the grid's own cellValue, not
        // this.priceRows[row].PriceType - fixes a bug reported 2026-09-03:
        // tabbing straight out of the very cell that just set PriceType
        // (e.g. blank -> "2") still saw the *old* value here and wrongly
        // skipped Variety, because this.priceRows (a plain external array)
        // had not been written yet at the moment this handler ran - the
        // grid's own internal edit state already knew the new value by
        // then, cellValue() reads that live state directly. Tabbing away,
        // then back, then out again "worked" only because enough time had
        // passed for the external array to catch up in between - a real
        // timing bug, not something to work around by waiting.
        const priceType = grid.cellValue(row, "PriceType");
        if (
          this.isCellEditable(columns[col].dataField, { PriceType: priceType })
        ) {
          target = { row, col };
          break;
        }
      }

      if (!target) return;
      keyEvent.preventDefault();

      // Must explicitly commit the cell being left before jumping away.
      // Fixes a bug reported 2026-09-03: YTD Price (a typed dxNumberBox,
      // unlike the select-box columns whose value already commits on
      // selection, before Tab is ever pressed) reset back to 0 on Tab out
      // instead of keeping what was typed. preventDefault() above stops the
      // browser's own native Tab action (moving DOM focus away) - which is
      // exactly what would have blurred the NumberBox's actual <input> and
      // fired its own onValueChanged/setCellValue commit. With that native
      // blur never happening, the typed value never reaches priceRows at
      // all - grid.saveEditData() was tried first and did not help, since
      // in cell-edit mode there is nothing "pending" in DevExtreme's own
      // tracked edit state to save; the value is stuck inside the
      // still-focused editor's own DOM input, not yet handed to the grid.
      // Fixed by forcing that blur manually instead - the same
      // activeElement.blur() + $nextTick idiom onSaveClicked already uses
      // for the identical reason (flush whatever's mid-edit before reading
      // priceRows) - rather than relying on the browser's native Tab action
      // we just prevented.
      const activeElement = document.activeElement;
      if (activeElement) activeElement.blur();
      this.$nextTick(() => {
        this.moveFocusToCell(grid, target.row, target.col);
      });
    },

    onGridContentReady(e) {
      if (e) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.setRightGridHeight();
          }, 500);
        });
      }

      // Consumes onStartClicked's one-shot flag - focuses row 0/column 0
      // (Price Type) the first time the grid renders after a successful
      // START, then never again (see the flag's own comment).
      if (this.focusFirstCellAfterGridReady) {
        this.focusFirstCellAfterGridReady = false;
        const grid = this.$refs.rightGrid;
        if (grid && grid.instance) {
          this.moveFocusToCell(grid.instance, 0, 0);
        }
      }
    },

    // Verified against the real gc-split-view.vue markup this session
    // (".split-container-child.right-container") rather than copied from
    // either sibling as-is - grower-syndicate-splits.vue's own equivalent
    // method queries ".filter-split-container", which does not match that
    // component's real output and so silently no-ops there.
    setRightGridHeight() {
      setTimeout(() => {
        const rightPanel = document.querySelector(".filter-split-container");

        if (!this.showRightPanel || !rightPanel) return;
        const formHeight = document.querySelector(
          ".selected-record-info",
        ).clientHeight;

        this.rightGridHeight =
          rightPanel.clientHeight - formHeight - 10 - 40 - 12 - 12;

        this.$nextTick(() => {
          const grid = this.$refs.rightGrid;
          if (grid && grid.instance) {
            grid.instance.updateDimensions();
          }
        });
      }, 200);
    },

    // §5/§7 (BR-05/BR-07/BR-08): whole-grid Save gate - the only enforcement
    // point for every grid-row rule client-side, matching
    // grower-syndicate-splits.vue's own validateBeforeSave discipline (Bug
    // #77235 - rows are never blocked from being added/edited/removed in the
    // grid itself; every rule is checked once, here, against the full row
    // set). AlmondcoSchemePriceBusinessRulesValidator re-checks all of this
    // server-side regardless (same messages) - this is UX, not the
    // authority.
    //
    // Filters out the always-present trailing blank row (PriceType `null` -
    // see ensureTrailingBlankRow) first - it is not a row the user has
    // entered anything into yet, so it must never count towards BR-08's
    // empty-grid check nor be treated as a row with missing Variety/Item
    // Code; onSaveClicked applies the same filter before building the
    // submitted Rows payload.
    validateBeforeSave() {
      if (!this.selectedRecord || !this.selectedRecord.SchemeId) {
        return "Select a Scheme+Season before saving.";
      }
      const rows = this.priceRows.filter((r) => r.PriceType);
      if (rows.length === 0) {
        return "Scheme Price has no detail. Please add one or more lines before attempting to save again";
      }

      for (const row of rows) {
        const hasVariety = !!row.VarietyCode;
        const hasItemCode = !!row.ItemCode;

        if (row.PriceType === "1" && (hasVariety || hasItemCode)) {
          return "Price Type 1 rows cannot have a Variety or Item Code.";
        }
        if (row.PriceType === "2" && !hasVariety) {
          return "Variety is required for Price Type 2.";
        }
        if (row.PriceType === "2" && hasItemCode) {
          return "Price Type 2 rows cannot have an Item Code.";
        }
        if (row.PriceType === "3" && (!hasVariety || !hasItemCode)) {
          return "Variety and Item Code are required for Price Type 3.";
        }
        if (!(parseFloat(row.YtdPrice) >= 0)) {
          return "YTD Price cannot be less than 0.";
        }
      }

      // Bug #77841: message now names the offending combination (matching
      // AlmondcoSchemePriceBusinessRulesValidator's own server-side wording)
      // and points at the grid highlight (onPriceRowPrepared/
      // duplicatePriceRowKeys) instead of a bare "something is duplicated"
      // alert with no indication of which rows or values are at fault.
      // Developer feedback (Muhamad Hanif, 2026-09-11): only include the
      // fields that are actually meaningful for the group's Price Type -
      // Variety/Item Code are always blank by design for Type 1 (BR-05), and
      // Item Code is always blank/disabled for Type 2, so showing them as
      // empty brackets was uninspiring/confusing. Type 3 genuinely populates
      // both, so its message is unchanged.
      const duplicateGroups = this.getDuplicatePriceRowGroups();
      if (duplicateGroups.length > 0) {
        const { priceType, varietyCode, itemCode } = duplicateGroups[0];
        const parts = [`Price Type [${priceType}]`];
        if (priceType !== "1") parts.push(`Variety [${varietyCode}]`);
        if (priceType === "3") parts.push(`Item Code [${itemCode}]`);
        return `The combination of ${parts.join(", ")} has been entered more than once - see the highlighted rows below.`;
      }

      return null;
    },

    // Bug #77841: single source of truth for "which rows collide on Price
    // Type+Variety+Item Code" - shared by validateBeforeSave's blocking
    // message and duplicatePriceRowKeys' grid highlight, so the message and
    // the visual cue can never disagree. Trimmed/case-insensitive, same
    // discipline as AlmondcoSchemePriceBusinessRulesValidator's own BR-07
    // check. Excludes the always-present trailing blank row the same way
    // validateBeforeSave does.
    getDuplicatePriceRowGroups() {
      const rows = this.priceRows.filter((r) => r.PriceType);
      const groups = new Map();
      for (const row of rows) {
        const priceType = (row.PriceType || "").trim().toUpperCase();
        const varietyCode = (row.VarietyCode || "").trim().toUpperCase();
        const itemCode = (row.ItemCode || "").trim().toUpperCase();
        const combo = `${priceType}|${varietyCode}|${itemCode}`;
        if (!groups.has(combo)) {
          groups.set(combo, { priceType, varietyCode, itemCode, rows: [] });
        }
        groups.get(combo).rows.push(row);
      }
      return [...groups.values()].filter((group) => group.rows.length > 1);
    },

    // Bug #77841: DxDataGrid's own row-prepared event - highlights every row
    // that duplicatePriceRowKeys flags, so the offending rows are visually
    // obvious in the grid itself before Save is attempted, not just named in
    // the blocking alert. Runs on every row (including re-runs as rows are
    // edited/removed), so it also clears the highlight once a duplicate is
    // resolved.
    onPriceRowPrepared(e) {
      if (e.rowType !== "data") return;
      if (this.duplicatePriceRowKeys.has(e.data.RowKey)) {
        e.rowElement.classList.add("duplicate-price-row");
        e.rowElement.setAttribute(
          "title",
          "This Price Type/Variety/Item Code combination is entered more than once in this grid.",
        );
      } else {
        e.rowElement.classList.remove("duplicate-price-row");
        e.rowElement.removeAttribute("title");
      }
    },

    // itemSaveCallBack(isValid, brokenRules) per GcFormHeaderToolbar's own
    // save button contract - isValid/brokenRules describe the toolbar's own
    // (empty, here) form, not our grid, so both are unused.
    async onSaveClicked() {
      const activeElement = document.activeElement;
      if (activeElement) activeElement.blur();

      await this.$nextTick();
      const blockingMessage = this.validateBeforeSave();
      if (blockingMessage) {
        await this.$alert(blockingMessage, "Unable to Save");
        return;
      }

      this.showLoading(true);
      try {
        const payload = {
          SchemeId: this.selectedRecord.SchemeId,
          SeasonYear: this.selectedRecord.SeasonYear,
          // #76649: now genuinely reflects Add New - baseView.mode stays
          // "create" for the whole session, before and after START (see
          // createIdentityLocked/isEditing), matching prototype.html's own
          // creating flag never flipping away. This is what
          // AlmondcoSchemePriceBusinessRulesValidator's create-mode
          // duplicate-price-set check (Task 76645) gates on server-side -
          // still the authoritative guard even though onStartClicked now
          // also checks (see its own comment) - a stale check result
          // between START and Save is still caught here.
          IsCreate: this.baseView.mode === "create",
          // Filters out the always-present trailing blank row (PriceType
          // `null`) the same way validateBeforeSave does - it is never a
          // real row to submit, and the SaveModel's own [Required] on
          // PriceType would reject it server-side if it were.
          Rows: this.priceRows
            .filter((r) => r.PriceType)
            .map((r) => ({
              PriceType: r.PriceType,
              VarietyCode: r.VarietyCode || "",
              ItemCode: r.ItemCode || "",
              YtdPrice: r.YtdPrice,
            })),
        };

        await AlmondcoSchemePriceService.saveSchemePrice(payload);

        if (this.baseView.mode === "create") {
          // #76649: unlike Edit, there is no existing RecordId in the route
          // yet for a create-mode Save - resolve the newly-created price
          // set's own server-computed RecordId via getSchemePriceSummaryByKey
          // (the same AlmondcoSchemePriceSummary this screen already reads
          // everywhere else, filtered on the exact SchemeId+SeasonYear just
          // saved instead of a RecordId), then route there - same end result
          // as grower-syndicate-splits.vue's own Add New flow, reached via a
          // different lookup since this screen's identity isn't a picked
          // existing entity.
          const summaryRes =
            await AlmondcoSchemePriceService.getSchemePriceSummaryByKey(
              this.selectedRecord.SchemeId,
              this.selectedRecord.SeasonYear,
            );
          const summaryRows = (summaryRes.data && summaryRes.data.value) || [];
          const newRecordId = summaryRows[0] && summaryRows[0].RecordId;
          this.setIgnoreRouteChangeConfirmation();
          await this.$router.push({
            path: `${this.leftGridConfig.pageRoute}/review/${newRecordId}`,
          });
        } else {
          // RecordId is a deterministic hash of SchemeId+SeasonYear (AD-04/
          // AD-05) - both are read-only/locked once a price set is open
          // (BR-04) and Save never changes them, so the route's own RecordId
          // is still valid after an Edit save.
          this.setIgnoreRouteChangeConfirmation();
          await this.$router.push({
            path: `${this.leftGridConfig.pageRoute}/review/${this.baseView.id}`,
          });
        }
      } catch (err) {
        var data =
          (err && err.data) || (err && err.response && err.response.data);
        var message = "An error occurred while saving the Scheme Price.";
        if (Array.isArray(data) && data.length > 0) {
          message = data
            .map(function (e) {
              return e.ErrorMessage || e;
            })
            .join(" ");
        } else if (typeof data === "string" && data.length > 0) {
          message = data;
        }
        await this.$alert(message, "Unable to Save");
      } finally {
        this.showLoading(false);
        this.setRightGridHeight();
        if (this.baseView.id) {
          this.loadSelectedRecord(this.baseView.id);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.scheme-prices {
  .split-grid-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: 12px;
    color: #667085;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .selected-record-info {
    padding: 12px 0px 12px 0px;

    .dx-form {
      background-color: white;
      padding: 12px;
    }

    // #76649: spacing above the create-mode identity form's inline START
    // button (identityFormItems), matching the vertical rhythm of the
    // Season/Scheme fields it sits below.
    .scheme-price-start-group {
      margin-top: 8px;
    }
  }

  .split-view-right-grid {
    padding: unset !important;
  }

  // Bug #77841: visual cue for a duplicated Price Type/Variety/Item Code
  // combination - onPriceRowPrepared toggles this on every affected row.
  // Targets the cells (not just the <tr>) - DevExtreme's own alternating-row
  // shading (dx-row-alt) sets an opaque background directly on each <td>,
  // which otherwise sits on top of and hides a row-level background here.
  .duplicate-price-row {
    > td {
      background-color: rgba(217, 45, 32, 0.08) !important;
    }

    &:hover > td {
      background-color: rgba(217, 45, 32, 0.14) !important;
    }
  }
}
</style>
